/**
 * Composable that tracks the browser's online/offline status and exposes helpers for
 * guarding async actions against network unavailability.
 *
 * - `isOnline` — shared reactive boolean (`useState`).
 * - `init()` — attaches `online`/`offline` window listeners (call once per app lifecycle).
 * - `checkConnectivity(url, timeoutMs)` — actively probes a URL via HEAD request.
 * - `requireOnline(action)` — executes `action` only when online; throws `'OFFLINE'` otherwise.
 */
export const useOffline = () => {
	const isOnline = useState('net:isOnline', () => true)
	const listenersReady = useState('net:listenersReady', () => false)
	const nuxtApp = useNuxtApp()

	const tr = (key: string) => {
		return nuxtApp.$i18n?.global?.t ? nuxtApp.$i18n.global.t(key) : key
	}

	const getToast = () => {
		try {
			return useToast()
		} catch {
			return null
		}
	}

	const notifyOffline = () => {
		const toast = getToast()
		if (toast) {
			toast.add({
				title: tr('No internet connection'),
				description: tr('Please check your connection and try again.'),
				color: 'red',
				timeout: 4000,
			})
		} else {
			console.warn('[offline] No internet connection')
		}
	}

	const updateFromNavigator = () => {
		if (!import.meta.client) return
		const next = navigator.onLine
		const prev = isOnline.value
		isOnline.value = next
		if (prev && !next) notifyOffline()
	}

	const init = () => {
		if (!import.meta.client || listenersReady.value) return
		updateFromNavigator()
		window.addEventListener('online', updateFromNavigator)
		window.addEventListener('offline', updateFromNavigator)
		listenersReady.value = true
	}

	const checkConnectivity = async (url = '/favicon.ico', timeoutMs = 3000) => {
		if (!import.meta.client) return true
		const ctrl = new AbortController()
		const t = setTimeout(() => ctrl.abort(), timeoutMs)
		try {
			await fetch(url, { method: 'HEAD', cache: 'no-store', signal: ctrl.signal })
			isOnline.value = true
			return true
		} catch {
			isOnline.value = false
			return false
		} finally {
			clearTimeout(t)
		}
	}

	async function requireOnline<T>(action: () => Promise<T> | T): Promise<T> {
		if (!import.meta.client) return await Promise.resolve(action())
		if (!navigator.onLine) {
			notifyOffline()
			throw new Error('OFFLINE')
		}
		return await action()
	}

	return { isOnline, init, requireOnline, checkConnectivity }
}
